import { useAuthStore } from '@/stores/auth'
import { accountQueries } from '@/queries/account'
import { animalQueries } from '@/queries/animal'
import { queryClient } from '@/queries/queryClient'
import { subscriptionQueries } from '@/queries/subscription'
import { FeedingPlanType, SubscriptionStatus } from '@lyka-pet-food/contracts'
import type { Subscription, User, Address } from '@repo/api-types'
import type { DogDto } from '@lyka-pet-food/contracts'

export type AccountPlanType = 'full_bowl' | 'half_bowl'

/**
 * Account-level plan type derived from a subscription's first feeding plan.
 * Bet spec: full_bowl when the first plan is full-bowl, otherwise half_bowl; null when no plans.
 * Uses the first plan because the /dogs page surfaces a single account-level plan.
 */
export const accountPlanType = (subscription: Subscription | null): AccountPlanType | null => {
  const first = subscription?.feedingPlans?.[0]
  if (!first) return null
  return first.type === FeedingPlanType.full_bowl ? 'full_bowl' : 'half_bowl'
}

/**
 * Picks the subscription to attribute analytics to: the active one if present,
 * otherwise the most recently created. Mirrors the active ?? most-recent selection
 * used by useSubscriptions(); kept pure (no setup context) so getSuperProperties can
 * call it from outside component setup.
 */
export const pickPrimarySubscription = (
  subscriptions: Subscription[] | undefined | null,
): Subscription | null => {
  if (!subscriptions || subscriptions.length === 0) return null
  const active = subscriptions.find((s) => s.status === SubscriptionStatus.active)
  if (active) return active
  return [...subscriptions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0] ?? null
}

// --- vue-query cache readers (best-effort; guarded so analytics never breaks app flows) ---

export const readProfile = (): User | undefined => {
  try {
    return queryClient.getQueryData<User>(accountQueries.profile().queryKey)
  } catch {
    return undefined
  }
}

export const readSubscriptions = (): Subscription[] => {
  try {
    const data = queryClient.getQueryData<{ subscriptions: Subscription[] }>(subscriptionQueries.list().queryKey)
    return data?.subscriptions ?? []
  } catch {
    return []
  }
}

export const readDogs = (): DogDto[] => {
  try {
    return queryClient.getQueryData<DogDto[]>(animalQueries.list().queryKey) ?? []
  } catch {
    return []
  }
}

export const readAddress = (): Address | undefined => {
  try {
    return queryClient.getQueryData<Address>(accountQueries.defaultAddress().queryKey)
  } catch {
    return undefined
  }
}

export const readIsAuthenticated = (): boolean => {
  try {
    return useAuthStore().isAuthenticated
  } catch {
    return false
  }
}
